import { Component, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { Router, ActivatedRoute } from '@angular/router';
import { TestResult } from './shared';
import { RestService } from '../services/rest.service';
import { MappingTelemetryService } from '../services/mapping-telemetry.service';
import { TranslatePipe } from '../pipes/translate.pipe';
import { CheckinProgressComponent } from '../shared/components/checkin-progress/checkin-progress.component';
import { TranslationService } from '../services/translation.service';
import { LoginService } from '../services/login.service';
import { RecallEncodeComponent } from './sharp-v2/components/recall-encode/recall-encode.component';
import { ConstellationCatchComponent } from './sharp-v2/components/constellation-catch/constellation-catch.component';
import { WordGardenComponent } from './sharp-v2/components/word-garden/word-garden.component';
import { EchoChamberComponent } from './sharp-v2/components/echo-chamber/echo-chamber.component';
import { MemorySceneComponent } from './sharp-v2/components/memory-scene/memory-scene.component';
import { SharpStateService } from './sharp-v2/services/sharp-state.service';
import { RECALL_POOLS } from './sharp-v2/sharp-v2.types';
import { TestInProgressComponent } from '../guards/test-in-progress.guard';

// SHARP short battery (handoff 2026-08-17): five stages only. shapeSorter,
// vigilance and the Stage-B decision gate that offered them are removed — the
// Stage-B screen had nothing left to offer once its two activities were deleted.
// The composite score never used those two tasks (COMPOSITE_TASKS = the four
// below + no shapeSorter/vigilance), so the score is unchanged.
type Phase =
  | 'intro'
  | 'recall-encode'
  | 'constellation'
  | 'word-garden'
  | 'echo-chamber'
  | 'memory-scene'
  | 'transition'
  | 'saving';

interface PhaseStep {
  phase: Phase;
  testId?: string;
}

@Component({
  selector: 'app-cognitive',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    TranslatePipe,
    RecallEncodeComponent,
    ConstellationCatchComponent,
    WordGardenComponent,
    EchoChamberComponent,
    MemorySceneComponent,
    CheckinProgressComponent,
  ],
  templateUrl: './cognitive.component.html',
  styleUrls: ['./cognitive.component.scss'],
})
export class CognitiveComponent implements OnDestroy, TestInProgressComponent {
  phase: Phase = 'intro';
  results: TestResult[] = [];
  scanId = '';
  showWhy = false;
  // QA render-only preview (route data.preview): no scanId, nothing is POSTed,
  // finishing loops back to the intro so the flow can be re-run.
  preview = false;

  // Transition screen (handoff 2026-08-17 part D #5 + copy section 6): after the
  // battery the app shows a MEANING screen, not an end — vision + hearing come
  // next. recallBandKey is the personal 5-band delayed-recall line derived from
  // the memory-scene free/cued scores. Results persist in the background the
  // moment the battery ends, so nothing is lost while she reads this screen.
  recallBandKey = '';
  continueBusy = false;
  private savePromise: Promise<void> | null = null;

  /**
   * Autosave for the battery. Until now the five tasks lived in the tab and
   * nothing was written until the last one finished, so a phone call, a refresh
   * or a browser eviction sent her back to task one - the mapping was protected
   * by its own draft, the exercises were not.
   *
   * The unit that is kept is one completed task. A task interrupted halfway is
   * not resumable in any honest way (a reaction run with a gap in it is not the
   * same measurement), so the task in flight is the most that can be lost. The
   * draft never touches scan.cognitive; the finish POST clears it.
   */
  resumePhase: Phase | null = null;
  /** how many of the five tasks the draft already holds (shown on the intro) */
  resumeDone = 0;
  private draftSaving = false;
  /** a draft older than this is dropped rather than offered: picking a run up
   *  from yesterday would mix two sittings into one score */
  private static readonly DRAFT_MAX_AGE_MS = 24 * 60 * 60 * 1000;
  /** past this gap the delayed recall is measuring a longer interval than the
   *  battery intends, so the run is flagged instead of silently compared */
  private static readonly LONG_BREAK_MS = 20 * 60 * 1000;

  private readonly mainFlow: PhaseStep[] = [
    { phase: 'recall-encode', testId: 'recall-encode' },
    { phase: 'constellation', testId: 'reaction' },
    { phase: 'word-garden', testId: 'word-garden' },
    { phase: 'echo-chamber', testId: 'echo-chamber' },
    { phase: 'memory-scene', testId: 'memory-scene' },
  ];

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private restService: RestService,
    private telemetry: MappingTelemetryService,
    private loginService: LoginService,
    private state: SharpStateService,
    private translationService: TranslationService
  ) {
    this.preview = !!this.route.snapshot.data['preview'];
    this.route.params.subscribe((params) => {
      this.scanId = params['scanId'];
      this.loadPriorMaterial();
      void this.loadDraft();
    });
  }

  /**
   * Step2 change7: seed material rotation from the user's earlier scans, so the
   * word-garden category and the recall items are fresh (a returning user isn't
   * re-tested on the same material, which would read as a practice-effect
   * "improvement"). Guests / first scans have no history → random material, as
   * before. Fetched during the intro screen so it's ready before the pickers run.
   */
  private async loadPriorMaterial(): Promise<void> {
    if (!this.scanId) return;
    try {
      const res = await this.restService.request<{ data: any[] }>({
        method: 'GET',
        path: `scan/${this.scanId}/user-data`,
        throwError: true,
        // Guest funnel: this endpoint requires a user session, so a guest 401s
        // here. It is best-effort (seed prior material) and must NOT bounce the
        // user to /login — handle the 401 locally as "no history".
        skipAuthRedirect: true,
      });
      const scans = (res?.data || [])
        .filter(
          (s: any) =>
            s?.guid !== this.scanId &&
            Array.isArray(s?.cognitive) &&
            s.cognitive.length
        )
        .sort(
          (a: any, b: any) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        );
      this.state.loadPriorMaterial(scans.map((s: any) => s.cognitive));
    } catch {
      // Guest / no session / no history → random material (unchanged behaviour).
    }
  }

  /** B5: in-section line for the shared check-in progress header, e.g.
   *  "Words · 3 of 5". The global step bar is driven by [step] only. */
  get cogDetail(): string {
    const of = this.translationService.translate('cognitive.progress.of');
    return `${this.stepLabel} · ${this.currentTestNumber} ${of} ${this.totalTests}`;
  }

  get stepLabel(): string {
    switch (this.phase) {
      case 'intro': return '';
      case 'recall-encode': return this.translationService.translate('miscUi.stepLabel.memorize');
      case 'constellation': return this.translationService.translate('miscUi.stepLabel.speed');
      case 'word-garden': return this.translationService.translate('miscUi.stepLabel.words');
      case 'echo-chamber': return this.translationService.translate('miscUi.stepLabel.memory');
      case 'memory-scene': return this.translationService.translate('miscUi.stepLabel.recall');
      case 'saving': return this.translationService.translate('miscUi.stepLabel.saving');
      default: return '';
    }
  }

  ngOnDestroy(): void {}

  /**
   * In progress once the user leaves the intro and until the run is saved.
   * Nothing is persisted mid-test, so leaving now discards everything (guarded
   * by `testInProgressGuard`).
   */
  isTestInProgress(): boolean {
    // 'transition' is post-battery: results are already persisting, so leaving
    // there must not be blocked/discarded (and the Continue CTA navigates away).
    return (
      this.phase !== 'intro' &&
      this.phase !== 'saving' &&
      this.phase !== 'transition'
    );
  }

  startTests(): void {
    // Resuming picks up at the first task the draft does not already hold; the
    // completed ones are restored, not re-run.
    this.phase = this.resumePhase || this.mainFlow[0].phase;
    this.resumePhase = null;
    window.scrollTo(0, 0);
  }

  // ---- autosave ------------------------------------------------------------

  /** Read the draft for this scan (server first, local fallback) and arm the
   *  intro to continue instead of restart. Best effort: any failure just means
   *  the battery starts from the top, which is today's behaviour. */
  private async loadDraft(): Promise<void> {
    if (!this.scanId || this.preview) return;
    let draft: any = null;
    try {
      const res: any = await this.restService.request({
        method: 'GET',
        path: `scan/${this.scanId}/new-test/exercise-draft`,
        throwError: true,
        skipAuthRedirect: true,
      });
      draft = res?.cognitive || null;
    } catch {
      /* no session / offline: the local copy below is the fallback */
    }
    if (!draft) draft = this.readLocalDraft();
    this.applyDraft(draft);
  }

  private applyDraft(draft: any): void {
    if (!draft || typeof draft !== 'object') return;
    const results = Array.isArray(draft.results) ? draft.results : [];
    if (!results.length) return;
    const at = new Date(draft.at || 0).getTime();
    const age = Date.now() - at;
    if (!(age >= 0) || age > CognitiveComponent.DRAFT_MAX_AGE_MS) {
      this.clearLocalDraft();
      return;
    }
    // The next task is the first one the draft has no result for.
    const doneIds = new Set(results.map((r: any) => r?.testId));
    const next = this.mainFlow.find((step) => !doneIds.has(step.testId));
    if (!next) return; // every task is in the draft: the finish POST owns it

    // The cued recall has to cue the items she actually memorised, so the
    // material travels with the draft instead of being re-randomised.
    const items = draft.recallItems;
    const itemsUsable =
      !!items &&
      typeof items === 'object' &&
      (['name', 'color', 'place', 'object'] as const).every(
        (k) => typeof items[k] === 'string' && RECALL_POOLS[k].includes(items[k])
      );
    // The delayed recall cues the material by value, through a translation key
    // built from it. If a release changed the pools while the draft was sitting
    // there, the saved material can no longer be rendered - and cueing her on
    // freshly picked items she never saw would be worse than restarting. So a
    // draft that holds an encode step it cannot restore is dropped whole,
    // before anything is taken from it.
    if (doneIds.has('recall-encode') && !itemsUsable) {
      this.clearLocalDraft();
      return;
    }

    this.results = results;
    this.resumeDone = this.mainFlow.filter((step) =>
      doneIds.has(step.testId)
    ).length;
    this.resumePhase = next.phase;
    if (itemsUsable) {
      for (const k of ['name', 'color', 'place', 'object'] as const) {
        this.state.recallItems[k] = items[k];
      }
    }
    this.state.validityFlags = Array.isArray(draft.validityFlags)
      ? [...draft.validityFlags]
      : [];
    this.state.sessionStart =
      typeof draft.sessionStart === 'number' ? draft.sessionStart : at;
    // A long break stretches the encode-to-recall interval, which makes the
    // delayed recall harder than the battery intends. Her work is kept either
    // way; the run is marked so the number is never read as a like-for-like
    // comparison.
    if (
      age > CognitiveComponent.LONG_BREAK_MS &&
      doneIds.has('recall-encode') &&
      !doneIds.has('memory-scene')
    ) {
      this.state.addValidityFlag(
        'Resumed after a break: recall interval extended'
      );
    }
  }

  /** Persist what is finished so far. Optimistic and never blocking: a failure
   *  falls back to the local copy, which the next load picks up. */
  private async saveDraft(): Promise<void> {
    if (!this.scanId || this.preview || this.draftSaving) return;
    this.draftSaving = true;
    const payload = {
      results: this.results,
      recallItems: { ...this.state.recallItems },
      validityFlags: [...this.state.validityFlags],
      sessionStart: this.state.sessionStart,
      at: new Date().toISOString(),
    };
    try {
      await this.restService.request({
        method: 'POST',
        path: `scan/${this.scanId}/new-test/exercise-draft`,
        body: { kind: 'cognitive', draft: payload },
        throwError: true,
        skipAuthRedirect: true,
      });
      this.clearLocalDraft();
    } catch {
      this.writeLocalDraft(payload);
    } finally {
      this.draftSaving = false;
    }
  }

  private get localKey(): string {
    return `cog_draft_${this.scanId}`;
  }

  private writeLocalDraft(payload: unknown): void {
    try {
      localStorage.setItem(this.localKey, JSON.stringify(payload));
    } catch {
      /* storage blocked or full: the server copy is the real one anyway */
    }
  }

  private readLocalDraft(): any | null {
    try {
      const raw = localStorage.getItem(this.localKey);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }

  private clearLocalDraft(): void {
    try {
      localStorage.removeItem(this.localKey);
    } catch {}
  }

  get currentTestNumber(): number {
    const idx = this.mainFlow.findIndex((s) => s.phase === this.phase);
    return idx >= 0 ? idx + 1 : 0;
  }

  get totalTests(): number {
    return this.mainFlow.length;
  }

  /** Top progress bar fill: stage N of 5 -> N/5 (handoff 2026-08-17, part D). */
  get progressPct(): number {
    return this.totalTests
      ? Math.round((this.currentTestNumber / this.totalTests) * 100)
      : 0;
  }

  get isInTestSequence(): boolean {
    return (
      this.phase !== 'intro' &&
      this.phase !== 'saving' &&
      this.phase !== 'transition'
    );
  }

  onTestComplete(result: TestResult): void {
    // section 43: which activity finished and how long it took. No responses.
    this.telemetry.track('cognitive_activity_completed', {
      task: result.testId,
      correct: !!result.correct,
      responseMs: Math.round(result.responseTime || 0),
    });
    this.results.push(result);

    const idx = this.mainFlow.findIndex((s) => s.testId === result.testId);
    if (idx >= 0 && idx < this.mainFlow.length - 1) {
      // one task done: keep it, so an interruption from here costs the task in
      // flight at most, never the whole battery
      void this.saveDraft();
      this.phase = this.mainFlow[idx + 1].phase;
      window.scrollTo(0, 0);
      return;
    }

    // Last task done. Derive the personal recall line, show the transition
    // screen, and persist the results in the background (not on QA preview).
    this.recallBandKey = this.computeRecallBand(result);
    this.phase = 'transition';
    window.scrollTo(0, 0);
    if (!this.preview) {
      this.savePromise = this.persistResults();
    }
  }

  /**
   * The 5-band delayed-recall line (copy section 6). `free` = items recalled on
   * her own (0-4); the cue recovers some of the missed ones. Every band is
   * suffixed in the template with the shared "this is your starting point".
   */
  private computeRecallBand(result: TestResult): string {
    const r: any = (result && result.response) || {};
    const free = typeof r.freeScore === 'number' ? r.freeScore : 0;
    const cued = typeof r.cuedScore === 'number' ? r.cuedScore : free;
    const missed = 4 - free;
    const returned = Math.max(0, cued - free); // missed items the cue brought back
    const base = 'cognitive.sharpV2.transition.band';
    if (free >= 4) return base + 'AllFree';
    if (free === 3) return base + 'MostFree';
    if (missed > 0 && returned >= missed) return base + 'CueAll';
    if (returned > 0) return base + 'CueSome';
    return base + 'CueNone';
  }

  /** POST the battery results. Errors are logged and swallowed so the Continue
   *  CTA still advances (matches the old save-and-navigate behaviour). */
  private async persistResults(): Promise<void> {
    if (!this.scanId) return;
    const payload = {
      cognitive: this.results,
      cognitiveMeta: {
        version: 'sharp-v2',
        validityFlags: this.state.validityFlags,
      },
    };
    try {
      await this.restService.request({
        method: 'POST',
        path: `scan/${this.scanId}/new-test`,
        body: payload,
      });
      // the finished battery replaces the draft (the server drops its own copy
      // in the same write)
      this.clearLocalDraft();
    } catch (error) {
      console.error('CognitiveComponent: save failed', error);
    }
  }

  /** Transition CTA: continue to the next step (vision + hearing) via the hub. */
  async continueFromTransition(): Promise<void> {
    if (this.continueBusy) return;

    // QA preview: nothing to persist, loop back to the intro so it can re-run.
    if (this.preview) {
      this.results = [];
      this.recallBandKey = '';
      this.phase = 'intro';
      window.scrollTo(0, 0);
      return;
    }

    this.continueBusy = true;
    this.phase = 'saving';
    try {
      if (this.savePromise) await this.savePromise;
    } finally {
      this.continueBusy = false;
    }
    // Return to the test hub (mirrors symptoms-form). The hub re-checks what's
    // done and advances to the next step (SENSE) — going straight to results
    // here skipped the SENSE test entirely.
    if (!this.scanId) {
      this.router.navigate(['/app/new-test']);
      return;
    }
    this.router.navigate(['/app/new-test'], {
      queryParams: { scanId: this.scanId },
    });
  }

  goBack(): void {
    this.router.navigate(['/app/new-test'], {
      queryParams: { scanId: this.scanId },
    });
  }

  isAdmin(): boolean {
    return this.loginService.isAdmin();
  }

  jumpTo(phase: Phase): void {
    if (!this.isAdmin() && !this.preview) return;
    this.phase = phase;
    window.scrollTo(0, 0);
  }

  jumpablePhases: Phase[] = [
    'recall-encode',
    'constellation',
    'word-garden',
    'echo-chamber',
    'memory-scene',
  ];
}
